import React, { useEffect, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import API_BASE_URL from "../../../../Constants";

// Reuse any existing components:
import ListHeader from "../../../../components/ListHeader/NoCardView/index";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";

/**
 * A page that:
 *   1) Fetches *paginated* Inspections for agency=1002 (NYC DOB),
 *   2) Optionally includes a search param (?q=...),
 *   3) For each inspection, fetches its devices,
 *   4) “Pivots” data so each device is a single row with Cat1, Cat5, Periodic columns,
 *   5) Renders a table with the same look/feel as your InspectionsList,
 *   6) Includes a top ListHeader (with search, Excel button, etc.) and a bottom pagination row.
 */
export default function NYCDobDevicesPivotPage() {
  // Hard-code Agency #1002 for “NYC DOB”
  const AGENCY_ID = 1002;

  // If you want to search, store a searchQuery
  const [searchQuery, setSearchQuery] = useState("");

  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Inspections data (fetched from /with-joins-listdto)
  const [inspections, setInspections] = useState([]);
  // Device pivot map & final rows
  const [deviceMap, setDeviceMap] = useState({});
  const [deviceRows, setDeviceRows] = useState([]);

  // Loading spinner
  const [isLoading, setIsLoading] = useState(false);

  // 1) Fetch paginated inspections for agency=1002 whenever page/rows/search changes
  useEffect(() => {
    fetchDobInspections();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  const fetchDobInspections = async () => {
    try {
      setIsLoading(true);

      const params = new URLSearchParams();
      params.append("agencyId", AGENCY_ID);
      params.append("page", currentPage - 1); // 0-based
      params.append("size", rowsPerPage);
      if (searchQuery) {
        params.append("q", searchQuery);
      }

      const url = `${API_BASE_URL}/api/inspections/with-joins-listdto?${params.toString()}`;
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setInspections(data.content || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching DOB inspections:", err);
      setInspections([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  // 2) For each inspection => fetch devices => pivot them into deviceMap
  useEffect(() => {
    if (!inspections.length) {
      setDeviceMap({});
      setDeviceRows([]);
      return;
    }

    const localMap = {};

    const gatherAllDevices = async () => {
      for (let insp of inspections) {
        try {
          const devUrl = `${API_BASE_URL}/api/inspections/id/${insp.inspectionId}/devices`;
          const devRes = await fetch(devUrl, { credentials: "include" });
          if (!devRes.ok) {
            throw new Error(
              `Error fetching devices for insp #${insp.inspectionId}`
            );
          }
          const devData = await devRes.json(); // array of BuildingDevices

          // For each device, fill in pivot columns for the test type
          devData.forEach((dev) => {
            const { deviceId, deviceType, deviceReference } = dev;

            // If we haven't seen this device yet, initialize
            if (!localMap[deviceId]) {
              localMap[deviceId] = {
                deviceId,
                deviceType,
                deviceReference,
                buildingReference: insp.buildingReference || "",
                managementCompanyName: insp.managementCompanyName || "",

                // Columns for Cat1, Cat5, Periodic
                cat1Last: "",
                cat1Next: "",
                cat5Last: "",
                cat5Next: "",
                perLast: "",
                perNext: "",
              };
            }

            // Pivot logic: if the test is “Cat 1”, fill cat1 columns, etc.
            const testType = (insp.test || "").toLowerCase();
            const pivotRow = localMap[deviceId];
            if (testType.includes("cat 1")) {
              pivotRow.cat1Last = insp.lastDate || "";
              pivotRow.cat1Next = insp.nextDate || "";
            } else if (testType.includes("cat 5")) {
              pivotRow.cat5Last = insp.lastDate || "";
              pivotRow.cat5Next = insp.nextDate || "";
            } else if (
              testType.includes("per") ||
              testType.includes("periodic")
            ) {
              pivotRow.perLast = insp.lastDate || "";
              pivotRow.perNext = insp.nextDate || "";
            }
          });
        } catch (err) {
          console.error("Error in gatherAllDevices loop:", err);
        }
      }

      // Convert map to array
      setDeviceMap(localMap);
      setDeviceRows(Object.values(localMap));
    };

    gatherAllDevices();
  }, [inspections]);

  // 3) Searching
  const handleSearch = () => {
    // Reset to page 1
    setCurrentPage(1);
    // Fetch again with new `searchQuery`
    fetchDobInspections();
  };
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 4) Excel Export
  const handleExportExcel = () => {
    // Since we statically import XLSX & saveAs, we can just call them:
    const worksheet = XLSX.utils.json_to_sheet(deviceRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "NYCDobDevicesPivot.xlsx"
    );
  };

  // 5) Pagination
  const handlePageChange = (event, page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="mx-auto pl-4 pr-4 min-h-screen w-full pt-4">
      {/* Top bar using your ListHeader */}
      <ListHeader
        title="NYC DOB Dates and Fines"
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearch={handleSearch}
        viewMode="table"
        handleViewModeChange={() => {}}
        handleOpenCreateModal={() => {}}
      >
        {/* Excel Button in the header */}
        <button
          type="button"
          onClick={handleExportExcel}
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg text-sss-blue-sel font-bold hover:bg-sss-blue-sel hover:text-white transition"
        >
          Excel
        </button>
      </ListHeader>

      <hr className="my-3 border-gray-200" />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <ColorRing
            visible
            height="200"
            width="200"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
          />
        </div>
      )}

      {/* The pivot table */}
      {!isLoading && deviceRows.length > 0 && (
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Device ID
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Device Ref
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Device Type
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Building
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Mgmt Co
              </th>

              {/* Cat 1 */}
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Cat1 Last
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Cat1 Next
              </th>

              {/* Cat 5 */}
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Cat5 Last
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Cat5 Next
              </th>

              {/* Periodic */}
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Periodic Last
              </th>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                Periodic Next
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {deviceRows.map((row, idx) => (
              <tr key={idx} className="border-t hover:bg-gray-50 transition">
                <td className="px-4 py-2 whitespace-nowrap">{row.deviceId}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.deviceReference || ""}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.deviceType || ""}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.buildingReference}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {row.managementCompanyName}
                </td>

                {/* Cat1 */}
                <td className="px-4 py-2 whitespace-nowrap">{row.cat1Last}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.cat1Next}</td>

                {/* Cat5 */}
                <td className="px-4 py-2 whitespace-nowrap">{row.cat5Last}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.cat5Next}</td>

                {/* Periodic */}
                <td className="px-4 py-2 whitespace-nowrap">{row.perLast}</td>
                <td className="px-4 py-2 whitespace-nowrap">{row.perNext}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* If no results (and not loading) */}
      {!isLoading && deviceRows.length === 0 && (
        <div className="p-4 text-center text-gray-600">
          No devices found for Agency #{AGENCY_ID} with the current filters.
        </div>
      )}

      {/* Pagination Row */}
      {!isLoading && deviceRows.length > 0 && (
        <div className="flex justify-between items-center p-4 border-t border-gray-300 bg-white">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={handleRowsPerPageChange}
          />
        </div>
      )}
    </div>
  );
}
